/**
 * Regla ÚNICA de cobro (servidor y pantalla usan esta misma función). Todos los montos están en $ REALES, la
 * moneda de los documentos desde la regla de precios 2026-10-09; el `factor` es siempre 1 (las divisas valen 1:1)
 * y se conserva solo como parámetro.
 *
 * 1. IGTF (Providencia SNAT/2022/000013): el sujeto pasivo especial percibe el IGTF (hoy 3 %) sobre el monto
 *    pagado en DIVISAS (efectivo $, Zelle, USDT) que cancela el documento (IVA incluido): 3 % de la parte del
 *    documento cubierta en divisas, SIN redondear: se redondea una sola vez, al llevarlo a bolívares (Resolución BCV
 *    21-08-01). No se cobra sobre sí mismo ni forma parte de la base del IVA.
 *    Los pagos en bolívares no causan IGTF y se aplican primero; pero si el vuelto sale en bolívares, esos bolívares
 *    no pagaron el documento: la parte en divisas crece y el IGTF se recalcula.
 *    El abono o anticipo entra completo al saldo, sin IGTF, y el impuesto se cobra cuando ese saldo paga una FAC o ND:
 *    la parte del saldo que entró en divisas sin IGTF cuenta como divisas (`saldoQuePagaComoDivisa`); el resto del
 *    saldo (nota de crédito, reintegro, abono viejo) no (pedido de la dueña 2026-10-19, reemplaza el IGTF al recibir el
 *    anticipo de la auditoría fiscal del 2026-09-24).
 *    Su importe fiscal en Bs lo guarda el servidor a la tasa BCV del día del cobro.
 * 1b. Vuelto ENTREGADO (respuesta de la clienta 2026-10-09): el cajero dice cuánto vuelto dio en $ y cuánto en Bs
 *    (efectivo, pago móvil o transferencia). Lo que no devuelve queda como "sobrante por vuelto". Los Bs devueltos no
 *    pagaron el documento, salgan como salgan: cuentan para el IGTF igual que en el reparto automático.
 * 2. Tolerancia de redondeo: medio centavo de $ cuando alguna forma de pago está en $ (se escribe con 2
 *    decimales). Si TODO el pago es en bolívares la tolerancia es medio céntimo de Bs (`tolerancia`): el cliente
 *    paga exactamente los Bs que dice el papel.
 * 3. FACTURA de contado (regla de la dueña del 2026-10-15, spec 05): el precio en Bs depende de cómo paga el cliente
 *    (`modoPrecioFactura`) y el IGTF es el 3 % de lo entregado en divisas que paga el PRECIO, sin el IVA, a lo sumo el
 *    precio (`igtfTope`). Pagar en divisas es como un descuento: la factura sale al precio real a tasa BCV.
 */
import { Money, type MoneyInput } from './money/index.ts';

/**
 * Cuánto del saldo a favor usado en un cobro paga como divisas para el IGTF (pedido de la dueña 2026-10-19): solo en un
 * cobro con IGTF (FACTURA o NOTA DE DÉBITO) y solo la parte del saldo que entró en divisas sin IGTF (los abonos desde
 * ese cambio), que se gasta primero. El saldo de una nota de crédito, de un reintegro o de un abono viejo (que ya
 * descontó su IGTF) no paga IGTF otra vez. `usado` y `enDivisaSinIgtf` (lo que le queda al cliente de esa parte) en $
 * del saldo; el resultado también. Servidor y pantalla reparten con esto el saldo entre `divisa` y `noDivisa` de
 * `resolverCobro`. La regla de PRECIO del 2026-10-15 no cambia: cuenta todo el saldo como divisas.
 */
export function saldoQuePagaComoDivisa(usado: MoneyInput, enDivisaSinIgtf: MoneyInput, cobroConIgtf: boolean): Money {
  if (!cobroConIgtf) return Money.cero();
  return Money.parse(usado).min(Money.parse(enDivisaSinIgtf)).max(Money.cero());
}

export interface EntradaCobro {
  /** Saldo a cancelar del documento en $. */
  porCobrar: MoneyInput;
  /** Σ $ de los pagos que NO son divisa (Bs, punto, transferencia, pago móvil, saldo a favor fuera de la parte en divisas sin IGTF). */
  noDivisa: MoneyInput;
  /** Σ $ de los pagos en divisa (y del saldo a favor en divisas sin IGTF en un cobro con IGTF: `saldoQuePagaComoDivisa`). */
  divisa: MoneyInput;
  /** Siempre 1 (divisas 1:1 con los $ del documento). */
  factor: MoneyInput;
  /** % de IGTF que aplica a este cobro; null o 0 = no aplica (notas). */
  igtfPct: MoneyInput | null;
  /** Documento a crédito o abono: se aceptan pagos parciales. */
  parcial?: boolean;
  /**
   * $ entregados en efectivo $ y en efectivo Bs (de ahí sale el vuelto: primero $, luego Bs). Con esto el
   * vuelto en Bs deja de contar como pago en bolívares para el IGTF. Sin este dato no se ajusta.
   */
  efectivo?: { usd: MoneyInput; bs: MoneyInput };
  /** Tolerancia de redondeo en $; si falta, medio centavo de $. Pago solo en Bs: medio céntimo de Bs ÷ tasa. */
  tolerancia?: MoneyInput;
  /**
   * Vuelto que el cajero entrega de verdad: los dólares que da (billetes; con el factor valen en $ del documento) y el
   * equivalente en $ del documento de lo dado en Bs. Si viene, el
   * vuelto no se reparte solo y la diferencia con el excedente queda como sobrante. Sin este dato: reparto automático
   * (todo el excedente se devuelve, primero en $ y el resto en Bs) y no hay sobrante.
   */
  vuelto?: { usd: MoneyInput; bs: MoneyInput };
  /**
   * FACTURA de contado (regla del 2026-10-15): precio sin IVA en $ del documento. Con él, el IGTF es el 3 % de lo
   * entregado en divisas (y saldo en divisas sin IGTF, pedido de la dueña 2026-10-19) que paga el documento y su IGTF, sin el IVA:
   * a lo sumo este precio. Los Bs se aplican primero; el vuelto en $ sale de las divisas y el vuelto en Bs de los Bs
   * (esos Bs no pagaron nada: si eran todos, la parte de las divisas crece). Sin él rige la regla anterior (parte
   * cubierta en divisas).
   */
  igtfTope?: MoneyInput;
}

/**
 * Con qué precio sale una FACTURA de contado según cómo paga el cliente (regla de la dueña del 2026-10-15):
 * - `bs`: no entrega divisas. Bs = precio real × tasa 2 (la regla de siempre).
 * - `divisa`: entrega divisas y el resto del precio sin IVA (precio − divisas) no pasa del tope ($ 20). Es como un
 *   descuento: la factura sale al precio real a tasa BCV y el resto se paga en Bs a tasa BCV.
 * - `bcv`: entrega divisas y el resto pasa del tope. La factura sale al precio a BCV (precio real × tasa 2 = precio
 *   BCV × tasa BCV) y cada dólar en divisas cuenta contra el precio a BCV.
 * `divisa` es lo que el cliente entrega en divisas y en saldo a favor, en $; el tope se mide sobre la factura entera.
 */
export type ModoPrecio = 'bs' | 'divisa' | 'bcv';
export function modoPrecioFactura(e: { precioSinIva: MoneyInput; divisa: MoneyInput; tope: MoneyInput }): ModoPrecio {
  const D = Money.parse(e.divisa);
  if (!D.esPositivo()) return 'bs';
  return Money.parse(e.precioSinIva).sub(D).lte(e.tope) ? 'divisa' : 'bcv';
}

/**
 * Factor con el que un dólar en divisas se convierte a $ del documento: 1 al precio real; tasa BCV ÷ tasa 2 al precio a
 * BCV (cada dólar en divisas vale un dólar a BCV).
 */
export function factorDivisaDeModo(modo: ModoPrecio, tasaBcv: MoneyInput, tasa2: MoneyInput): Money {
  return modo === 'bcv' ? Money.parse(tasaBcv).div(tasa2) : Money.parse('1');
}

export interface ResultadoCobro {
  /** Σ de todo lo entregado en $ (incluye el IGTF). */
  total: Money;
  /** Parte del documento cancelada con divisas, en $. */
  igtfBase: Money;
  /** IGTF percibido en $. */
  igtf: Money;
  /** Base del IGTF en divisa (lo que se declara), 2 decimales. */
  igtfBaseDivisa: Money;
  /** IGTF en divisa (3 % de la base en divisa), 2 decimales. */
  igtfDivisa: Money;
  /** Lo que hay que entregar para cancelar el documento completo (saldo + IGTF si aplica), 2 dec. */
  requerido: Money;
  /** Lo que se abona al documento (sin IGTF), 2 dec. */
  aplicado: Money;
  /** Sobrante a devolver como vuelto (0 si cae dentro de la tolerancia). */
  excedente: Money;
  /** Lo que queda en caja porque no se devolvió todo el excedente ("sobrante por vuelto"); 0 sin vuelto explícito. */
  sobrante: Money;
  /** true = el vuelto declarado supera el excedente (no se puede devolver más de lo que sobra). */
  vueltoDeMas: boolean;
  /** Falta para cancelar el documento (0 si cubre). */
  faltante: Money;
  /** true = el documento queda cancelado con este cobro. */
  completo: boolean;
}

const MEDIO_CENTAVO = Money.parse('0.005');
/** Un centavo de $: holgura del vuelto declarado (los Bs devueltos se redondean al céntimo). */
const UN_CENTAVO = Money.parse('0.01');
const CERO = Money.cero();

/**
 * Tolerancia de redondeo de un cobro, en $: medio centavo de $ si alguna forma de pago está en $ (divisa, saldo a
 * favor o crédito de cambio); si todo se paga en bolívares, medio céntimo de Bs convertido con la tasa del cobro.
 */
export function toleranciaCobro(todoEnBs: boolean, tasaBs?: MoneyInput | null): Money {
  if (todoEnBs && tasaBs && Money.parse(tasaBs).esPositivo()) return MEDIO_CENTAVO.bsAUsd(tasaBs);
  return MEDIO_CENTAVO;
}

/**
 * IGTF de una parte del documento cubierta en divisas. La base es dinero entregado (céntimos); el IGTF es su % SIN
 * redondear (3 % de $ 19,50 = $ 0,585): se redondea una sola vez, al llevarlo a bolívares (Resolución BCV 21-08-01).
 */
export function igtfSobre(baseBcv: MoneyInput, factor: MoneyInput, igtfPct: MoneyInput | null): { baseDivisa: Money; igtfDivisa: Money; igtfBcv: Money } {
  const base = Money.parse(baseBcv);
  if (igtfPct === null || !base.esPositivo()) return { baseDivisa: CERO, igtfDivisa: CERO, igtfBcv: CERO };
  const r = Money.parse(igtfPct).div(100);
  const baseDivisa = base.quitarFactor(factor).redondear2();
  const igtfDivisa = baseDivisa.mul(r);
  return { baseDivisa, igtfDivisa, igtfBcv: igtfDivisa.aplicarFactor(factor) };
}

export function resolverCobro(e: EntradaCobro): ResultadoCobro {
  const P = Money.parse(e.porCobrar);
  const N = Money.parse(e.noDivisa);
  const D = Money.parse(e.divisa);
  const f = Money.parse(e.factor);
  const pct = e.igtfPct !== null && Money.parse(e.igtfPct).esPositivo() && D.esPositivo() ? e.igtfPct : null;
  const total = N.add(D);
  const tol = e.tolerancia === undefined ? MEDIO_CENTAVO : Money.parse(e.tolerancia);

  // Parte del documento cubierta en divisas, dado el vuelto que sale en Bs (esos Bs no pagaron el documento)
  const cubierta = (vueltoBs: Money) => P.sub(N.sub(vueltoBs).max(CERO).min(P)).max(CERO);
  // Vuelto declarado en $ del documento (los dólares devueltos valen con el factor)
  const explicito = e.vuelto ? { usd: Money.parse(e.vuelto.usd).aplicarFactor(f), bs: Money.parse(e.vuelto.bs) } : null;
  let vueltoBs = explicito ? explicito.bs : CERO;
  let base = cubierta(vueltoBs);
  let ig = igtfSobre(base, f, pct);
  const reglaFactura = e.igtfTope !== undefined && pct !== null;
  if (reglaFactura) {
    // Regla de la dueña (2026-10-15): 3 % de las divisas que pagan el documento y su IGTF, sin el IVA. Los Bs (netos del
    // vuelto en Bs) se aplican primero; las divisas (netas del vuelto en $) pagan el resto. Lo que sobra depende del
    // IGTF y el IGTF de lo que sobra: se resuelve en pocas vueltas
    const tope = Money.parse(e.igtfTope!).min(P);
    const efUsd = e.efectivo ? Money.parse(e.efectivo.usd) : null;
    const efBs = e.efectivo ? Money.parse(e.efectivo.bs) : CERO;
    const vueltoCon = (igtfDoc: Money) => {
      if (explicito) return explicito;
      // Reparto automático: primero en $ del efectivo $ recibido, el resto en Bs del efectivo Bs
      const sobra = total.sub(P).sub(igtfDoc).max(CERO);
      const usd = efUsd ? sobra.min(efUsd) : sobra;
      return { usd, bs: sobra.sub(usd).min(efBs) };
    };
    const baseDe = (igtfDoc: Money) => {
      const v = vueltoCon(igtfDoc);
      const bsNetos = N.sub(v.bs).max(CERO);
      return D.sub(v.usd).min(P.add(igtfDoc).sub(bsNetos)).max(CERO).min(tope);
    };
    // Se parte del IGTF más alto posible (todas las divisas pagan, hasta el tope) y se baja hasta el primer punto fijo:
    // así, cuando existe, sale la solución sin vuelto (desde cero se llegaba a otra con $ 0,01 de vuelto y 1 céntimo
    // menos de IGTF, y ese vuelto podía cambiar la regla de precio)
    base = D.min(tope);
    ig = igtfSobre(base, f, pct);
    for (let i = 0; i < 8; i++) {
      const b = baseDe(ig.igtfBcv);
      const n = igtfSobre(b, f, pct);
      const estable = n.igtfBcv.eq(ig.igtfBcv);
      // La base y el IGTF quedan de la misma vuelta (la base declarada es la que dio ese IGTF)
      base = b;
      ig = n;
      if (estable) break;
    }
  }
  if (!reglaFactura && !explicito && e.efectivo && pct !== null) {
    const efUsd = Money.parse(e.efectivo.usd);
    const efBs = Money.parse(e.efectivo.bs);
    for (let i = 0; i < 6; i++) {
      const sobra = total.sub(P).sub(ig.igtfBcv);
      if (!sobra.gt(tol)) break;
      const nuevoVueltoBs = sobra.sub(sobra.min(efUsd)).min(efBs).max(CERO);
      if (nuevoVueltoBs.eq(vueltoBs)) break;
      vueltoBs = nuevoVueltoBs;
      base = cubierta(vueltoBs);
      ig = igtfSobre(base, f, pct);
    }
  }
  const requeridoExacto = P.add(ig.igtfBcv);
  const diff = total.sub(requeridoExacto);

  if (diff.gte(tol.neg())) {
    const excedente = diff.gt(tol) ? diff : CERO;
    const entregado = explicito ? explicito.usd.add(explicito.bs) : excedente;
    const sobrante = explicito ? excedente.sub(entregado) : CERO;
    return {
      total,
      igtfBase: pct !== null ? base : CERO,
      igtf: ig.igtfBcv,
      igtfBaseDivisa: ig.baseDivisa,
      igtfDivisa: ig.igtfDivisa,
      requerido: requeridoExacto.redondear2(),
      aplicado: P.redondear2(),
      excedente,
      sobrante: sobrante.gt(MEDIO_CENTAVO) ? sobrante.redondear2() : CERO,
      vueltoDeMas: !!explicito && entregado.sub(excedente).gt(UN_CENTAVO),
      faltante: CERO,
      completo: true,
    };
  }
  // Factura de contado con la regla del 2026-10-15: no alcanza. El IGTF es el de las divisas entregadas (sin el IVA) y
  // lo demás abona el documento: lo que falta incluye ese IGTF
  if (reglaFactura) {
    return {
      total,
      igtfBase: base,
      igtf: ig.igtfBcv,
      igtfBaseDivisa: ig.baseDivisa,
      igtfDivisa: ig.igtfDivisa,
      requerido: requeridoExacto.redondear2(),
      aplicado: total.sub(ig.igtfBcv).redondear2(),
      excedente: CERO,
      sobrante: CERO,
      vueltoDeMas: !!explicito && explicito.usd.add(explicito.bs).gt(UN_CENTAVO),
      faltante: diff.neg().redondear2(),
      completo: false,
    };
  }
  // No alcanza: todo lo entregado se abona (si es parcial); las divisas cubren principal + su IGTF
  let igtfDivisa = CERO;
  let igtfBcv = CERO;
  let baseDivisa = CERO;
  if (pct !== null) {
    const r = Money.parse(pct).div(100);
    const dDivisa = D.quitarFactor(f);
    // Las divisas cubren principal + su IGTF: IGTF = D × r ÷ (1 + r), sin redondear (se redondea una vez, en Bs)
    igtfDivisa = dDivisa.mul(r).div(Money.parse('1').add(r));
    igtfBcv = igtfDivisa.aplicarFactor(f);
    baseDivisa = dDivisa.sub(igtfDivisa);
  }
  return {
    total,
    igtfBase: D.sub(igtfBcv),
    igtf: igtfBcv,
    igtfBaseDivisa: baseDivisa,
    igtfDivisa,
    requerido: requeridoExacto.redondear2(),
    aplicado: total.sub(igtfBcv).redondear2(),
    excedente: CERO,
    sobrante: CERO,
    // Sin cubrir el documento no hay nada que devolver
    vueltoDeMas: !!explicito && explicito.usd.add(explicito.bs).gt(UN_CENTAVO),
    faltante: diff.neg().redondear2(),
    completo: false,
  };
}

/**
 * Cuánto pedir en una forma de pago en divisa para cubrir un restante — botón "Resto": el restante más su IGTF.
 * `tope` (factura de contado, regla del 2026-10-15): el IGTF es el 3 % de las divisas que pagan el precio sin IVA, a lo
 * sumo `tope.precio`; `tope.divisa` es lo que ya se entregó en divisas, en $ del documento, cuyo IGTF ya está en el
 * restante. Hasta el tope, cada dólar nuevo paga 1 − 3 % del restante; pasado el tope, el restante más el IGTF que
 * falta hasta el tope.
 */
export function restanteEnDivisaBcv(restantePrincipal: MoneyInput, igtfPct: MoneyInput | null, factor: MoneyInput = '1', tope?: { precio: MoneyInput; divisa: MoneyInput }): Money {
  const R = Money.parse(restantePrincipal);
  if (!tope || igtfPct === null || !Money.parse(igtfPct).esPositivo() || !R.esPositivo()) return R.add(igtfSobre(R, factor, igtfPct).igtfBcv);
  const r = Money.parse(igtfPct).div(100);
  const precio = Money.parse(tope.precio);
  const yaEnDivisa = Money.parse(tope.divisa).min(precio);
  const hastaTope = R.div(Money.parse('1').sub(r));
  if (!yaEnDivisa.add(hastaTope).gt(precio)) return hastaTope;
  return R.add(precio.sub(yaEnDivisa).mul(r));
}
